<script setup>
/**
 * The board that opens beside a chip on the week grid.
 *
 * It answers the three questions somebody clicking a calendar entry actually
 * has — what is this, who is it for, and what is my part in it — and then
 * offers one way in whose label depends on the answer to the third. A single
 * "Join" would be a lie to the person hosting it.
 *
 * The same surface doubles as the board for an empty slot, which is how the
 * design board draws it: one popover, so a second one can never be open at
 * the same time as the first.
 *
 * Positioning, focus and dismissal belong to XlPopover. The `boundary` is
 * passed through because the grid's Monday column starts where the content
 * area does, and without it a popover anchored there lands on the sidebar.
 */
import { computed } from 'vue';
import { roomLabel } from '../../helpers/roomVisits';
import XlButton from '../xl/XlButton.vue';
import XlListRow from '../xl/XlListRow.vue';
import XlPopover from '../xl/XlPopover.vue';
import XlStatusIcon from '../xl/XlStatusIcon.vue';
import XlTag from '../xl/XlTag.vue';
import XlTooltip from '../xl/XlTooltip.vue';
import IconCalendar from '../icons/IconCalendar.vue';
import IconClockCountdown from '../icons/IconClockCountdown.vue';
import IconClose from '../icons/IconClose.vue';
import IconMicrophone from '../icons/IconMicrophone.vue';
import IconRepeat from '../icons/IconRepeat.vue';
import IconStar from '../icons/IconStar.vue';
import IconUserMultiple from '../icons/IconUserMultiple.vue';
import IconVideo from '../icons/IconVideo.vue';
import { isOfficeHours as isOfficeHoursKind } from '../../helpers/sessionKinds';
import {
  dayLabel,
  formatLabel,
  hasEnded,
  parseSessionDate,
  repeatLabel,
  speakerName,
  timeLabel,
  timeRangeLabel,
  datesLeftLabel,
  shortDate,
  windowLabel,
  DELETE_REFUSED,
} from '../../helpers/sessionCard';
import { compactDate } from '../../helpers/usDate';
import { bookFor, describeAccess, ownBookOf } from '../../composables/useResourceAccess';
import useAudienceNames from '../../composables/useAudienceNames';
import { useAuthStore } from '../../stores/auth';

const props = defineProps({
  open: { type: Boolean, default: false },
  /** The chip or cell that was clicked; the board hangs off it. */
  anchor: { type: [Object, null], default: null },
  /** The grid, so the board never lands on the sidebar beside it. */
  boundary: { type: [Object, null], default: null },
  /** A session, when a chip was clicked. */
  session: { type: [Object, null], default: null },
  /** `{ day, hour }`, when an empty cell was clicked. */
  emptySlot: { type: [Object, null], default: null },
  /** The signed-in person's local user id, for working out their part in it. */
  viewerId: { type: [Number, String, null], default: null },
  /** Whether this viewer may change the agenda at all. */
  canManage: { type: Boolean, default: false },
  canEdit: { type: Boolean, default: false },
  segments: { type: Array, default: () => [] },
  /** How many are waiting to be let into today's Office Hours; null when unknown. */
  waiting: { type: Number, default: null },
});

const emit = defineEmits(['update:open', 'join', 'edit', 'cancel', 'create', 'delete-desk']);

const isOfficeHours = computed(() => Boolean(props.session) && isOfficeHoursKind(props.session));
const isCancelled = computed(() => props.session?.status === 'cancelled');

const start = computed(() => parseSessionDate(props.session?.start_date));
const end = computed(() => parseSessionDate(props.session?.end_date));

const sameDay = (a, b) => a && b
  && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * "Today" and "running right now" are different questions and the footer asks
 * both: today decides how loud the way in is, live decides whether it is a way
 * back rather than a way in.
 */
const isToday = computed(() => sameDay(start.value, new Date()));
const isLive = computed(() => {
  const now = new Date();

  return Boolean(start.value && end.value && now >= start.value && now <= end.value);
});

const speakers = computed(() => (props.session?.session_speakers ?? []).map(speakerName).filter(Boolean));

const onDuty = computed(() =>
  speakerName(props.session?.moderator ?? props.session?.session_moderator) ?? 'Nobody');
const hasDesk = computed(() => (props.session?.moderator ?? props.session?.session_moderator) != null);

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

// `session_moderator` arrives as an object from the agenda resource and as a
// bare id from the create/edit payload, and the list attaches a resolved
// `moderator` on top of either. All three are the same person.
const hostId = computed(() => {
  const session = props.session ?? {};

  return session.moderator?.id ?? session.session_moderator?.id ?? session.session_moderator ?? null;
});
const isHost = computed(() => sameId(hostId.value, props.viewerId));
const isSpeaker = computed(() =>
  (props.session?.session_speakers ?? []).some((speaker) => sameId(speaker?.id, props.viewerId)));

/**
 * There is always a way in, and on the staff side it has one name whatever
 * your part in it — "Open the room", or "Back to the room" once it is
 * running. ("Join the call" is the portal's: a policyholder joins through the
 * waiting room; staff go straight in.)
 */
// "Back to the room" only for somebody who has been in it (roomVisits).
const joinLabel = computed(() => roomLabel(props.session?.id, isLive.value));

const roleLine = computed(() => {
  if (isHost.value) return "You're hosting this";
  if (isSpeaker.value) return "You're speaking";

  return `${onDuty.value} is hosting`;
});

/*
 * Office Hours on the board is one button, the same for everybody on the hub
 * side (prototype `calPop`): whose slot it is was never a reason to keep
 * somebody out of the call, and the desk is set up on its own schedule, not
 * edited from here. Anybody who opens it also sees who is waiting.
 * (A policyholder never reaches this board; theirs is the portal's.)
 */
const waitingLine = computed(() => {
  // Everyone who opens the board, not only whoever is on the desk: the owner
  // could not see somebody was waiting (Office Hours QA).
  if (!isOfficeHours.value || !isToday.value || props.waiting == null) return null;

  return props.waiting === 1 ? '1 waiting' : `${props.waiting} waiting`;
});

const roleHint = computed(() => (isToday.value
  ? 'the room is open now — you can go in early'
  : 'open the room any time to check your camera'));

const auth = useAuthStore();
const { members: hubMembers, segments: hubSegments } = useAudienceNames(() => props.session?.audience);
const audience = computed(() => describeAccess(props.session?.audience, props.segments.length ? props.segments : hubSegments.value, {
  book: props.session ? bookFor(props.session, auth.userData) : false,
  members: hubMembers.value,
  me: ownBookOf(auth.userData),
}));
const repeats = computed(() => repeatLabel(props.session));

/**
 * Whether the desk is open, said plainly. Open is inside its window *and*
 * somebody on the desk — an empty desk is closed whatever its hours say.
 */
const deskStatus = computed(() => {
  if (!isOfficeHours.value) return null;
  if (!hasDesk.value) return { open: false, text: 'Nobody on the desk — closed' };
  if (isLive.value) return { open: true, text: `Open now, until ${timeLabel({ start_date: props.session.end_date })}` };
  if (hasEnded(props.session)) return { open: false, text: 'Closed' };

  return { open: false, text: `Closed · opens ${isToday.value ? 'today' : dayLabel(props.session)} at ${timeLabel(props.session)}` };
});

/**
 * Every date of a series, in two lists in date order (QA): what already
 * happened, on its real day, plain; and what is coming up. A cancelled date is
 * listed with what is coming up, marked, so a run that lost one says so — one
 * already past is neither, and is not listed.
 */
const seriesDates = computed(() => (props.session?.series_schedule ?? []).map((date) => ({
  ...date,
  label: shortDate(date.start_date),
})));
const happened = computed(() => seriesDates.value.filter((date) => date.state === 'done'));
const comingUp = computed(() => seriesDates.value.filter((date) => date.state === 'next' || date.state === 'upcoming'
  || (date.state === 'cancelled' && new Date(date.starts_at ?? String(date.start_date).replace(' ', 'T')) > new Date())));
const cancelledAhead = computed(() => comingUp.value.filter((date) => date.state === 'cancelled').length);
const remainingLine = computed(() => datesLeftLabel(props.session.series_remaining)
  + (cancelledAhead.value ? ` · ${cancelledAhead.value} cancelled` : ''));

const formatLine = computed(() => (speakers.value.length ? speakers.value.join(', ') : 'No speakers yet'));

const slotDay = computed(() => compactDate(props.emptySlot?.day));

const slotTime = computed(() => {
  const hour = props.emptySlot?.hour;

  if (hour == null) return '';

  // Built through the same formatter the rest of the agenda uses, so the hour
  // reads identically to the chips it was clicked between.
  return timeLabel({ start_date: `2000-01-01 ${String(hour).padStart(2, '0')}:00:00` });
});


const close = () => emit('update:open', false);
const act = (event, payload) => {
  close();
  emit(event, payload);
};
</script>

<template>
  <XlPopover
    :open="props.open"
    :anchor="props.anchor"
    :boundary="props.boundary"
    labelled-by="session-popover-title"
    @update:open="emit('update:open', $event)"
  >
    <!-- An empty slot: a session that does not exist yet, so the board only
         confirms what was clicked before handing over to the form. -->
    <template v-if="props.emptySlot">
      <header class="popover__head" data-testid="popover-slot">
        <b id="session-popover-title" class="popover__title">New session</b>
        <button type="button" class="popover__close" aria-label="Close" @click="close">
          <IconClose color="currentColor" />
        </button>
      </header>

      <div class="popover__body">
        <XlListRow>
          <template #leading>
            <XlStatusIcon tone="brand" size="34"><IconCalendar color="currentColor" size="19" /></XlStatusIcon>
          </template>
          <template #title>{{ slotDay }}</template>
          <template #subtitle>{{ slotTime }} — you can change it on the next screen</template>
        </XlListRow>
      </div>

      <footer class="popover__foot">
        <XlButton variant="ghost" size="sm" @click="close">Cancel</XlButton>
        <XlButton size="sm" data-testid="popover-create" @click="act('create', props.emptySlot)">Set it up</XlButton>
      </footer>
    </template>

    <template v-else-if="props.session">
      <!-- The stripe is the only thing tying the board back to the colour of
           the chip it came from. -->
      <header
        class="popover__head popover__head--striped"
        :class="`popover__head--${isOfficeHours ? 'office-hours' : 'session'}`"
      >
        <span class="popover__heading">
          <b id="session-popover-title" class="popover__title">{{ props.session.title }}</b>
          <XlTag v-if="isCancelled" tone="error">Cancelled</XlTag>
          <span class="popover__when" data-testid="popover-when">
            {{ dayLabel(props.session) }} · {{ isOfficeHours ? windowLabel(props.session) : timeRangeLabel(props.session) }}<template v-if="!isCancelled && hasEnded(props.session)"> · <span data-testid="popover-happened">already happened</span></template>
          </span>
        </span>
        <button type="button" class="popover__close" aria-label="Close" @click="close">
          <IconClose color="currentColor" />
        </button>
      </header>

      <div class="popover__body">
        <template v-if="isOfficeHours">
          <XlListRow>
            <template #leading>
              <XlStatusIcon tone="success" size="32"><IconClockCountdown color="currentColor" size="18" /></XlStatusIcon>
            </template>
            <template #title>Drop-in Office Hours</template>
            <template #subtitle>
              <span data-testid="popover-duty">
                {{ isHost ? "You're on the desk" : hasDesk ? `${onDuty} is on the desk` : 'Nobody is on the desk' }}<template v-if="waitingLine"> · <b data-testid="popover-waiting">{{ waitingLine }}</b></template>
                <template v-else> · policyholders call in, no booking</template>
              </span>
            </template>
          </XlListRow>

          <p
            class="popover__desk-status"
            :class="{ 'popover__desk-status--open': deskStatus?.open }"
            data-testid="popover-desk-status"
          >{{ deskStatus?.text }}</p>

          <XlListRow v-if="repeats">
            <template #leading>
              <span class="popover__plain-icon"><IconRepeat color="currentColor" size="18" /></span>
            </template>
            <template #title>{{ repeats }}</template>
            <template #subtitle>edit this day to cover it, or the series to change the arrangement</template>
          </XlListRow>
        </template>

        <template v-else>
          <XlListRow>
            <template #leading>
              <XlStatusIcon tone="brand" size="32"><IconVideo color="currentColor" size="18" /></XlStatusIcon>
            </template>
            <template #title>{{ formatLabel(props.session) ?? 'Session' }}</template>
            <template #subtitle><span data-testid="popover-format">{{ formatLine }}</span></template>
          </XlListRow>

          <XlListRow>
            <template #leading>
              <span class="popover__plain-icon"><IconUserMultiple color="currentColor" size="18" /></span>
            </template>
            <template #title><span data-testid="popover-audience">{{ audience.label }}</span></template>
            <template #subtitle>no booking — they turn up if they want to</template>
          </XlListRow>

          <!-- What this viewer's part in it is, which is also what decides
               the label on the way in below. -->
          <XlListRow>
            <template #leading>
              <span class="popover__plain-icon">
                <IconStar v-if="isHost" color="currentColor" size="18" />
                <IconMicrophone v-else-if="isSpeaker" color="currentColor" size="18" />
                <IconUserMultiple v-else color="currentColor" size="18" />
              </span>
            </template>
            <template #title><span data-testid="popover-role">{{ roleLine }}</span></template>
            <template #subtitle>{{ roleHint }}</template>
          </XlListRow>

          <XlListRow v-if="repeats">
            <template #leading>
              <span class="popover__plain-icon"><IconRepeat color="currentColor" size="18" /></span>
            </template>
            <template #title>{{ repeats }}</template>
            <template #subtitle>
              <span data-testid="popover-series">{{ remainingLine }}</span>
            </template>
          </XlListRow>

          <!-- Opening a repeating session shows every date it has: what already
               happened, then what is coming up, one list each, in date order. -->
          <div v-if="happened.length || comingUp.length" class="popover__dates" data-testid="popover-series-dates">
            <template v-for="group in [{ key: 'happened', label: 'Already happened', dates: happened }, { key: 'coming', label: 'Coming up', dates: comingUp }]" :key="group.key">
              <div v-if="group.dates.length" class="popover__date-group" :data-testid="`popover-dates-${group.key}`">
                <p class="popover__date-heading">{{ group.label }}</p>
                <ul class="popover__date-list">
                  <li
                    v-for="date in group.dates"
                    :key="`${date.id ?? date.start_date}-${date.state}`"
                    class="popover__date"
                    :class="`popover__date--${date.state}`"
                    :data-state="date.state"
                  >{{ date.label }}<template v-if="date.state === 'next'"> · next</template><template v-else-if="date.state === 'cancelled'"> · cancelled</template></li>
                </ul>
              </div>
            </template>
          </div>
        </template>
      </div>

      <footer v-if="isOfficeHours" class="popover__foot" data-testid="popover-desk-foot">
        <XlButton
          v-if="!isCancelled"
          :variant="isToday ? 'success' : 'ghost'"
          size="sm"
          data-testid="popover-join"
          @click="act('join', props.session)"
        ><IconVideo color="currentColor" size="13" />{{ joinLabel }}</XlButton>
        <!-- Edit beside the way in, as on a session's card (Office Hours →
             "Add Edit to the card"): the hours and who is on the desk, for
             whoever the Sessions page lets edit them. Never Cancel. -->
        <XlButton
          v-if="!isCancelled && (props.canManage || props.canEdit)"
          variant="ghost"
          size="sm"
          data-testid="popover-edit"
          @click="act('edit', props.session)"
        >Edit</XlButton>
        <!-- Delete beside Edit, for the same people: the whole desk, after a
             dialog that says what goes (Sessions → "Add Delete to Office Hours"). -->
        <XlButton
          v-if="props.canManage || props.canEdit"
          variant="ghost-danger"
          size="sm"
          data-testid="popover-delete-desk"
          @click="act('delete-desk', props.session)"
        >Delete</XlButton>
      </footer>

      <footer v-else class="popover__foot">
        <!-- The way in carries the video mark, as on the list's row; the way
             back does not (prototype `calPop`). -->
        <XlButton
          v-if="!isCancelled"
          :variant="isLive ? 'danger' : isToday ? 'success' : 'ghost'"
          size="sm"
          data-testid="popover-join"
          @click="act('join', props.session)"
        ><IconVideo v-if="!isLive" color="currentColor" size="13" />{{ joinLabel }}</XlButton>

        <template v-if="props.canManage || props.canEdit">
          <XlButton v-if="!isCancelled" variant="ghost" size="sm" data-testid="popover-edit" @click="act('edit', props.session)">Edit</XlButton>
          <!-- One word for it everywhere: Delete. Cancel already means "leave
               it alone" on every form and dialog. -->
          <XlButton
            v-if="props.canManage"
            variant="ghost-danger"
            size="sm"
            data-testid="popover-cancel"
            @click="act('cancel', props.session)"
          >Delete</XlButton>
          <XlTooltip v-else :text="DELETE_REFUSED" control>
            <XlButton variant="ghost-danger" size="sm" unavailable data-testid="popover-cancel">Delete</XlButton>
          </XlTooltip>
        </template>
      </footer>
    </template>
  </XlPopover>
</template>

<style scoped>
/* XlPopover pads itself for the panels it was built for; this board runs its
   head and foot to the edges, so the padding is taken back and re-spent. */
:deep(.xl-popover) { padding: 0; }

.popover__head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 15px 16px 13px;
  font-family: var(--xl-font-family);
}

.popover__head--striped { border-left: 4px solid var(--xl-color-primary); }
.popover__head--office-hours { border-left-color: var(--xl-color-success, #166b48); }

.popover__heading { flex: 1; min-width: 0; }

.popover__title {
  font-size: 15.5px;
  line-height: 1.3;
  color: var(--xl-color-on-surface);
}

.popover__when {
  display: block;
  margin-top: 2px;
  font-size: 12.5px;
  color: var(--xl-color-outline);
}

.popover__close {
  display: grid;
  place-items: center;
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 9px;
  background: none;
  color: var(--xl-color-outline);
  cursor: pointer;
}

.popover__close:hover { background: var(--xl-color-background); }

.popover__body {
  padding: 0 16px 6px;
  font-family: var(--xl-font-family);
}

/* XlListRow is sized for a list inside a card; a popover is a tighter space,
   so the rows close up. Everything else about them — the rule between, the
   last one having none, the text block — is the component's. */
.popover__body :deep(.xl-list-row) { padding: 8px 0; }

.popover__body :deep(.xl-list-row__text b) { font-size: 13.5px; }

.popover__body :deep(.xl-list-row__text span) { font-size: 13px; }

/* The board tints only the row that says what the thing IS. The rest are bare
   icons in the same 32px box, which is how the design board draws them. */
.popover__plain-icon {
  display: grid;
  place-items: center;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  color: var(--xl-color-on-surface-variant);
}

/* Open or closed, in the desk's green when it is open. */
.popover__desk-status {
  margin: 4px 0 6px 44px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--xl-color-secondary-text);
}
.popover__desk-status--open { color: var(--xl-color-success, #166B48); }

/* Every date of a series: two lists, one date a line, scrolling past a
   handful of rows. */
.popover__dates {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 2px 0 6px 44px;
  max-height: 150px;
  overflow-y: auto;
}
.popover__date-heading {
  margin: 0 0 3px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--xl-color-secondary-text);
}
.popover__date-list { margin: 0; padding: 0; list-style: none; }
.popover__date {
  font-size: 13px;
  line-height: 1.6;
  color: var(--xl-color-on-surface);
  font-variant-numeric: tabular-nums;
}
.popover__date--done { color: var(--xl-color-secondary-text); }
.popover__date--next { font-weight: 600; color: var(--xl-color-primary); }
.popover__date--cancelled { color: var(--xl-color-error); }

.popover__foot {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  padding: 12px 16px;
  border-top: 1px solid var(--xl-color-border-subtle, var(--xl-color-border));
  background: var(--xl-color-background);
}
</style>
